/** Tasks group (canonical `LaJMLlbG`). Job-task CRUD. Callers re-fetch the job
 *  /detail after any write rather than reconciling the return value (the server
 *  recomputes deadlines + work_items + counts; brief §2.5). */

import { api } from "./client";
import type {
  JobTask,
  TaskCreateInput,
  TaskUpdateInput,
  TaskListFilters,
  TaskListResponse,
  TaskBoardFilters,
  TaskBoardRow,
  TaskBoardResponse,
  TaskMoveInput,
  TaskReorderInput,
  TaskType,
  StaffUser,
} from "@/types";

/** GET /staff-users — assignable owners for the drawers' Owner select (admin +
 *  staff roles; id + name, name-sorted). Staff-gated server-side. */
export function listStaffUsers(): Promise<StaffUser[]> {
  return api.get<StaffUser[]>("tasks", "staff-users");
}

/** GET /list — cross-job task list for the Tasks dashboard (omit job_id). With
 *  no `statuses`, hides done/cancelled unless include_done/include_cancelled. */
export function listTasks(filters: TaskListFilters = {}): Promise<TaskListResponse> {
  return api.get<TaskListResponse>("tasks", "list", {
    query: {
      page: filters.page,
      per_page: filters.per_page,
      sort_field: filters.sort_field,
      sort_direction: filters.sort_direction,
      job_id: filters.job_id,
      application_id: filters.application_id,
      assigned_to_user_id: filters.assigned_to_user_id,
      statuses: filters.statuses,
      kinds: filters.kinds,
      priorities: filters.priorities,
      due_window: filters.due_window,
      q: filters.q,
      include_done: filters.include_done || undefined,
      include_cancelled: filters.include_cancelled || undefined,
    },
  });
}

/** POST /create — new task. Set `job_id` to the current job. */
export function createTask(input: TaskCreateInput): Promise<JobTask> {
  return api.post<JobTask>("tasks", "create", { body: input });
}

/** PATCH /update — edit / complete (`status:"done"`) / soft-delete
 *  (`deleted:true`). All fields optional except `id`. */
export function updateTask(input: TaskUpdateInput): Promise<JobTask> {
  return api.patch<JobTask>("tasks", "update", { body: input });
}

/** The /board denormalised join returns the job_* and application_* columns as
 *  either a scalar or a single-element array, per row (a live Xano addon quirk —
 *  scalar when job_id is set, array otherwise). Coerce to the first scalar so
 *  the UI never has to. Leaves genuine nulls/scalars untouched. */
function firstScalar<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value.length ? (value[0] ?? null) : null;
  return value ?? null;
}

function normaliseBoardRow(row: TaskBoardRow): TaskBoardRow {
  return {
    ...row,
    job_number: firstScalar(row.job_number),
    job_name: firstScalar(row.job_name),
    job_postcode: firstScalar(row.job_postcode),
    job_status: firstScalar(row.job_status),
    application_reference: firstScalar(row.application_reference),
    application_type: firstScalar(row.application_type),
    application_status: firstScalar(row.application_status),
    // Addendum 2 — submit cards are non-draggable. Default an absent task_type
    // to manual (legacy rows).
    task_type: (firstScalar(row.task_type) as TaskType | null) ?? "manual",
    // 0 = never hand-placed; the board falls back to its computed order.
    board_position: typeof row.board_position === "number" ? row.board_position : 0,
  };
}

/** GET /board — flat working set for the Kanban (all open + recently-completed).
 *  Returns rows already bucketed-ready (caller groups by `board_stage`). */
export function getTaskBoard(filters: TaskBoardFilters = {}): Promise<TaskBoardRow[]> {
  return api
    .get<TaskBoardResponse>("tasks", "board", {
      query: {
        assigned_to_user_id: filters.assigned_to_user_id,
        job_id: filters.job_id,
        application_id: filters.application_id,
        completed_within_days: filters.completed_within_days,
      },
    })
    .then((res) => (res.items ?? []).map(normaliseBoardRow));
}

/** PATCH /move — change a card's lane. id goes in the BODY (not the path).
 *  Send only `board_stage`; the backend owns status/completed_at. Returns the
 *  raw task row (board_stage/status/completed_at are the fields to reconcile). */
export function moveTaskStage(input: TaskMoveInput): Promise<TaskBoardRow> {
  return api.patch<TaskBoardRow>("tasks", "move", { body: input });
}

/** PATCH /reorder — persist a lane's manual order: stamps a 1-based
 *  board_position onto each id, in the order sent. Always the FULL lane (see
 *  {@link TaskReorderInput}); ids that no longer resolve are skipped server-side. */
export function reorderBoard(input: TaskReorderInput): Promise<{ updated: number }> {
  return api.patch<{ updated: number }>("tasks", "reorder", { body: input });
}
